/**
 * `lazyWithStrings` — React.lazy that also pulls the lazily-split half of
 * the translation table.
 *
 * The i18n table was split so that keys only lazy code reads no longer sit
 * in the main bundle (see i18n/lazy.ts). `t()` is synchronous, so those
 * keys must be merged into `T` *before* the component that needs them
 * renders, or it would render raw key names like "community.flagContent".
 * Wrapping the import is what guarantees the ordering: both chunks are
 * requested together and the component resolves only once the strings have
 * been merged, so the extra chunk costs a parallel request, not a round
 * trip.
 *
 * Used by App.tsx and components/DetailPanel.tsx for every React.lazy
 * component. Enforced by src/i18n/split.test.ts.
 */
import React from "react";

// Mirrors React.lazy's own signature. Parameterising on the props type
// instead makes TypeScript infer `never` for them at every call site,
// because props sit in a contravariant position inside ComponentType.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithStrings<T extends React.ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  // `as const` keeps this a tuple. Without it TypeScript widens the array to
  // a union of both module shapes and every call site fails to type.
  return React.lazy(async () => {
    const [mod] = await Promise.all([load(), import("./lazy.ts")] as const);
    return mod;
  });
}
